/**
 * One credit request, actionable at a glance (§18.6, Figma 158:4274).
 *
 * Every figure that is a tenant credit goes through `Credits` (`../lib/units`)
 * so a null renders as the unpriced marker, never `0` or a dash (this plane's
 * hard constraint) -- `credits_requested` and the tenant's balance both
 * qualify, `accounts_to_track` and `slots_requested` do not (counts, not
 * credit figures).
 *
 * A `SLOTS` request (owner ruling 2026-09-20 on
 * `owner-decisions-fs-campaign-capacity-operator-slots-requests`) renders its
 * own kind and its own amount, in the organisation's own word for a campaign
 * slot ("vaga"/"vagas") -- never the raw `SLOTS` discriminator -- and carries
 * no `accounts_to_track` (`core` sets it null for that kind), so the accounts
 * badge is `CREDITS`-only.
 *
 * Claiming (`OPEN` -> `IN_REVIEW`) has no shared hook, so this is a local
 * mutation over the same `PATCH /ops/credit-requests/{r}` route
 * `useDecideRequest` calls, sending `state` alone. The route table names
 * `decision_note` alongside `state` but does not say whether it is required
 * on every transition, so omitting it for a bare claim is an assumption, not
 * a documented rule.
 *
 * The deck (`ops-overhaul-copy-deck.md` §6.6) has no key for "who is
 * reviewing this", for the unspecified-accounts fallback, or for the
 * movement link's own text; those three keep their pre-existing local
 * strings below rather than inventing new copy.
 */

import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Badge } from '@/ui/badge';
import { Button } from '@/ui/button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemHeader,
  ItemTitle,
} from '@/ui/item';
import { Separator } from '@/ui/separator';
import { Hint } from '@/chrome/hint/Hint';
import { Absent } from '../../chrome/measure';
import { useApiClient } from '../../app/session/client';
import { defineCatalogue, getLocale } from '../../i18n';
import { days, integer } from '../../i18n/format';
import type { ApiFailureError } from '../../lib/http/errors';
import { opsKeys } from '../api/queries';
import { creditRequestSchema, type CreditRequest } from '../api/schemas';
import { Credits } from '../lib/units';
import { DecisionDialog } from './DecisionDialog';

const catalogue = defineCatalogue(
  {
    'request.kind.credits': 'créditos',
    'request.kind.slots': 'vagas',
    'request.accounts.other': '{count} contas',
    'request.accounts.one': '{count} conta',
    'request.slots.other': '{count} vagas',
    'request.slots.one': '{count} vaga',
    'request.claim': 'Assumir',
    'request.claim.hint': 'Assuma, para que outros operadores saibam que você está cuidando disso.',
    'request.intendedAccounts.hint': 'O que a organização diz que planeja rastrear.',
    'request.context.hint': 'O que a organização está tentando ver, e por quê.',
    requesterLine: '{who} · Admin da organização',
    tenantBalanceLabel: 'Saldo atual',
    tenantBalanceUnknown: 'Saldo ainda não carregado',
    creditsRequestedLabel: 'Créditos pedidos',
    slotsRequestedLabel: 'Vagas pedidas',
    // Not in the deck (§6.6 has no key for either) -- carried over unchanged.
    claimedBy: 'Em análise por {who}',
    accountsNotSpecified: 'contas não informadas',
    slotsNotSpecified: 'vagas não informadas',
    movementLink: 'Ver movimentação',
  },
  {
    'request.kind.credits': 'credits',
    'request.kind.slots': 'slots',
    'request.accounts.other': '{count} accounts',
    'request.accounts.one': '{count} account',
    'request.slots.other': '{count} slots',
    'request.slots.one': '{count} slot',
    'request.claim': 'Claim',
    'request.claim.hint': 'Take it, so other operators know you are handling it.',
    'request.intendedAccounts.hint': 'What the organisation says it plans to track.',
    'request.context.hint': 'What the organisation is trying to see, and why.',
    requesterLine: '{who} · Organisation admin',
    tenantBalanceLabel: 'Current balance',
    tenantBalanceUnknown: 'Balance not loaded yet',
    creditsRequestedLabel: 'Credits requested',
    slotsRequestedLabel: 'Slots requested',
    claimedBy: 'Being reviewed by {who}',
    accountsNotSpecified: 'accounts not specified',
    slotsNotSpecified: 'slots not specified',
    movementLink: 'View movement',
  },
);
const t = catalogue.t;

/** `PATCH /ops/credit-requests/{r}  {state}` -- claim and decide share one
 *  route, one verb; a claim just omits `decision_note`. Kept local rather
 *  than added to `../api/queries.ts`. */
function useClaimRequest() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CreditRequest, ApiFailureError, string>({
    mutationFn: (requestId) =>
      client.patchParsed(
        `/ops/credit-requests/${requestId}`,
        { state: 'IN_REVIEW' },
        creditRequestSchema,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: opsKeys.requests(['OPEN', 'IN_REVIEW']) }),
  });
}

/** Whole days since `createdAt`, floored -- the queue's own age column, not
 *  `../lib/units`' funded-window reach (a different figure, same rounding
 *  direction for the same reason: never over-promise). */
function ageInDays(createdAt: string): number {
  return Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000);
}

export function RequestCard(props: {
  request: CreditRequest;
  tenantBalance: number | null;
  onChanged?: (request: CreditRequest) => void;
  /** Set by `Queue`, whose `ItemGroup` wrapper carries `role="list"` -- ARIA
   *  requires a `list`'s children to be `listitem`s, which a standalone card
   *  (rendered directly by its own tests and stories, with no `list`
   *  ancestor) must not claim to be. */
  asListItem?: boolean;
}): ReactNode {
  const claim = useClaimRequest();
  const locale = getLocale();
  const { request } = props;
  const firstContextLine = request.context.split('\n')[0] ?? '';

  return (
    <Item
      variant="outline"
      role={props.asListItem ? 'listitem' : undefined}
      className="flex-col items-stretch"
      data-testid="request-card"
      data-request-id={request.id}
    >
      <ItemHeader className="items-start">
        <div className="flex flex-col gap-1">
          <ItemTitle>
            {/* On the child, not on `ItemTitle`: that primitive merges its own
                raw Tailwind size with `className` through `cn()`, whose class
                table does not know `tokens.css`'s scale, so a token passed
                there arrives beside the raw size rather than replacing it. */}
            <span data-testid="request-tenant-name" className="text-title-sm">
              {request.tenant_name}
            </span>
          </ItemTitle>
          <span className="text-muted-foreground text-body-sm">
            {`${t('requesterLine', { who: request.requested_by })} · `}
            <span data-testid="age">{days(ageInDays(request.created_at), locale)}</span>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" data-testid="request-kind">
            {t(request.kind === 'SLOTS' ? 'request.kind.slots' : 'request.kind.credits')}
          </Badge>
          {request.kind === 'CREDITS' && (
            <>
              <Badge variant="secondary">
                {request.accounts_to_track === null
                  ? t('accountsNotSpecified')
                  : catalogue.plural('request.accounts.other', request.accounts_to_track, {
                      count: integer(request.accounts_to_track, locale),
                    })}
              </Badge>
              <Hint label={t('creditsRequestedLabel')}>{t('request.intendedAccounts.hint')}</Hint>
              <Badge variant="secondary" data-testid="credits-requested">
                <Credits value={request.credits_requested} unit="tenant" />
              </Badge>
            </>
          )}
          {request.kind === 'SLOTS' && (
            <Badge variant="secondary" data-testid="slots-requested">
              {request.slots_requested === null
                ? t('slotsNotSpecified')
                : catalogue.plural('request.slots.other', request.slots_requested, {
                    count: integer(request.slots_requested, locale),
                  })}
            </Badge>
          )}
        </div>
      </ItemHeader>

      <ItemContent>
        <div className="flex items-start gap-1">
          <ItemDescription className="line-clamp-none">{firstContextLine}</ItemDescription>
          <Hint label={firstContextLine}>{t('request.context.hint')}</Hint>
        </div>

        <p className="text-muted-foreground text-caption" data-testid="tenant-balance">
          {`${t('tenantBalanceLabel')}: `}
          {/* `balance_credits` is not nullable on the wire, so a null here is
              the register unresolved or refused, never an unpriced figure --
              `Credits` would say "sem preço" about an organisation nothing
              measured. */}
          {props.tenantBalance === null ? (
            <Absent reason={{ text: t('tenantBalanceUnknown') }} />
          ) : (
            <Credits value={props.tenantBalance} unit="tenant" />
          )}
        </p>

        {request.decided_by !== null && (
          <p data-testid="claimed-by" className="text-muted-foreground text-body-sm">
            {t('claimedBy', { who: request.decided_by })}
          </p>
        )}
      </ItemContent>

      {(request.state === 'OPEN' ||
        request.state === 'IN_REVIEW' ||
        (request.state === 'GRANTED' &&
          (request.granted_movement_id !== null || request.granted_slot_movement_id !== null))) && (
        <ItemFooter className="flex-col items-stretch gap-3">
          <Separator />
          <ItemActions className="justify-end">
            {request.state === 'OPEN' && (
              <>
                <Button
                  type="button"
                  onClick={() =>
                    claim.mutate(request.id, { onSuccess: (updated) => props.onChanged?.(updated) })
                  }
                >
                  {t('request.claim')}
                </Button>
                <Hint label={t('request.claim')}>{t('request.claim.hint')}</Hint>
              </>
            )}

            {request.state === 'IN_REVIEW' && (
              <DecisionDialog
                request={request}
                onDecided={(updated) => props.onChanged?.(updated)}
              />
            )}

            {/* `/tenants/$tenantId` (`../tenant/routes.ts`) is this plane's only
                registered tenant route -- there is no per-movement path or anchor,
                so this links to the tenant screen, whose ledger (`../tenant/
                MovementLedger.tsx`) is where the granted movement itself lives. */}
            {request.state === 'GRANTED' &&
              (request.granted_movement_id !== null ||
                request.granted_slot_movement_id !== null) && (
                <Link to="/tenants/$tenantId" params={{ tenantId: request.tenant_id }}>
                  {t('movementLink')}
                </Link>
              )}
          </ItemActions>
        </ItemFooter>
      )}
    </Item>
  );
}
